import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import VehicleCard from './VehicleCard';
import PageLoader from './ui/PageLoader';
import { FavoritesSkeleton } from './ui/Skeletons';
import EmptyState from './ui/EmptyState';
import Button from './ui/Button';
import { PageHeader } from './ui/PageHeader';
import PageShell from './ui/PageShell';
import type { FavoriteVehicleCard } from '../services/api/favorites';
import type { PaginationMeta } from '../types/discoveryBadges';
import { vehicleProfilePath } from '../utils/vehicleUrls';

const FavoriteBuildsPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [vehicles, setVehicles] = useState<FavoriteVehicleCard[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 20,
    total_count: 0,
    total_pages: 0,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location.pathname + location.search }, replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, location.pathname, location.search]);

  const load = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.listFavorites({ page: pageNum, limit: 20, sort: 'recent' });
      setVehicles(result.data);
      setPagination(result.pagination);
      setPage(pageNum);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load favorites');
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    void apiService.trackEvent('favorites_page_viewed');
    void load(1);
  }, [authLoading, isAuthenticated, load]);

  const handleRemove = (vehicleId: number) => {
    setVehicles((prev) => prev.filter((v) => v.id !== vehicleId));
    setPagination((prev) => ({
      ...prev,
      total_count: Math.max(0, (prev.total_count || 0) - 1),
    }));
  };

  if (authLoading || (!isAuthenticated && !error)) {
    return <PageLoader message="Loading…" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <PageShell contentClassName="!py-6 sm:!py-8">
        <PageHeader
          eyebrow="Saved"
          title="Favorite builds"
          description="Private to you. Builds worth coming back to."
          backLink={{ label: 'Back to Garage', to: '/garage' }}
        />

        {loading ? <FavoritesSkeleton fullPage={false} cardCount={6} /> : null}

        {error ? (
          <EmptyState
            title="Couldn&apos;t load favorites"
            description={error}
            action={
              <Button type="button" variant="primary" className="min-h-[44px]" onClick={() => load(page)}>
                Try again
              </Button>
            }
          />
        ) : null}

        {!loading && !error && vehicles.length === 0 ? (
          <EmptyState
            imageSrc="/images/brand/story-mountain-road.jpg"
            imageAlt=""
            title="No saved builds yet"
            description="Bookmark builds worth coming back to."
            action={
              <Button type="button" variant="primary" onClick={() => navigate('/discover')}>
                Browse builds
              </Button>
            }
          />
        ) : null}

        {!loading && vehicles.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {vehicles.map((v) => (
                <VehicleCard
                  key={v.id}
                  vehicle={{
                    id: v.id,
                    year: v.year,
                    make: v.make,
                    model: v.model,
                    trim: v.trim || '',
                    nickname: v.nickname || undefined,
                    modification_count: v.modification_count || 0,
                    display_name: v.display_name || `${v.year} ${v.make} ${v.model}`,
                    owner_username: v.owner_username,
                    owner_display_name: v.owner_display_name,
                    image_url: (v.image_url as string) || undefined,
                    is_public: v.is_public,
                    created_at: v.created_at || '',
                    updated_at: v.updated_at,
                    favorite_count: v.favorite_count ?? 1,
                    is_favorited: true,
                    view_count: v.view_count,
                    ...(v.public_slug ? { public_slug: v.public_slug } : {}),
                  } as any}
                  collectionMode
                  showOwner
                  showFavorite
                  onVehicleClick={(vehicle) => {
                    void apiService.trackEvent('favorite_build_opened', {
                      vehicle_id: vehicle.id,
                      metadata: { source: 'favorites_page' },
                    });
                    if (vehicle.owner_username) {
                      navigate(
                        vehicleProfilePath(
                          vehicle.owner_username,
                          (v.public_slug as string) || String(vehicle.id)
                        )
                      );
                    }
                  }}
                  onUserClick={(username) => navigate(`/${username}`)}
                  onFavoriteChange={(favorited) => {
                    if (!favorited) handleRemove(v.id);
                  }}
                />
              ))}
            </div>

            {pagination.total_pages > 1 ? (
              <div className="flex items-center justify-center gap-3 mt-8">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={page <= 1 || loading}
                  onClick={() => load(page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-400" aria-live="polite">
                  Page {page} of {pagination.total_pages}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={page >= pagination.total_pages || loading}
                  onClick={() => load(page + 1)}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
    </PageShell>
  );
};

export default FavoriteBuildsPage;
